import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { environment } from 'src/environments/environment';
import {
  GetPackageHistoryBody,
  GetPackageInquiryBody,
} from '../../models/package-tracking.model';
import { CommonUtils } from '../../utils/common/common.utils';
import { ErrorhandleService } from '../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private readonly addStopSubject = new Subject<void>();
  addedStop$ = this.addStopSubject.asObservable();

  private readonly editPendingStopSubject = new Subject<void>();
  editPendingStop$ = this.editPendingStopSubject.asObservable();

  private readonly pageHeaderSubject = new BehaviorSubject<string>('');
  pageHeader$ = this.pageHeaderSubject.asObservable();

  baseUrl = environment.apiUrl.concat('package-management/');
  constructor(
    private readonly http: HttpClient,
    private readonly commonUtils: CommonUtils,
    private readonly errorHandler: ErrorhandleService,
  ) {}

  notifyStopAdded() {
    this.addStopSubject.next();
  }

  notifyPendingStopAdded() {
    this.editPendingStopSubject.next();
  }

  setPageHeader(header: string) {
    this.pageHeaderSubject.next(header);
  }

  /**
   *Gets package details based on passed in tracking id
   * @param {string} trackingID
   * @return {*}  {Observable<PackageInquiry[]>}
   * @memberof DataService
   */
  getPackageDetailsById(
    trackingID: string,
    locationCode: string,
  ): Observable<GetPackageInquiryBody> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<GetPackageInquiryBody>(this.baseUrl.concat('package-inquiry'), {
        params: { locationCode: locationCode, trackingNumber: trackingID },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GetPackageInquiryBody>(
            error,
            error.status,
            'Data Service: Get Package Inquiry',
            BANNER_TYPE.PACKAGE_DETAILS_ERROR,
          ),
        ),
      );
  }

  /**
   *Return currently stored package history
   * @return {*}
   * @memberof DataService
   */
  getPackageHistory(
    trackingID: string,
    locationCode: string,
  ): Observable<GetPackageHistoryBody> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<GetPackageHistoryBody>(this.baseUrl.concat('package-history'), {
        params: { locationCode: locationCode, trackingNumber: trackingID },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GetPackageHistoryBody>(
            error,
            error.status,
            'Data Service: Get Package History',
            BANNER_TYPE.PACKAGE_HISTORY_ERROR,
          ),
        ),
      );
  }

  private readonly INFO_CACHE_KEY = 'ops.infoNotice.cache';

  private loadInfoCache(): Record<string, string> {
    try {
      return JSON.parse(sessionStorage.getItem(this.INFO_CACHE_KEY) || '{}');
    } catch {
      return {};
    }
  }

  private saveInfoCache(cache: Record<string, string>) {
    sessionStorage.setItem(this.INFO_CACHE_KEY, JSON.stringify(cache));
  }

  setInfoNotice(stopId: number, infoNotice: string | null | undefined): void {
    const cache = this.loadInfoCache();
    const key = String(stopId);
    const val = (infoNotice ?? '').trim();

    if (!val) {
      delete cache[key];
    } else {
      cache[key] = val;
    }
    this.saveInfoCache(cache);
  }

  getInfoNotice(stopId: number): string | null {
    const cache = this.loadInfoCache();
    const key = String(stopId);
    return cache[key] ?? null;
  }

  clearInfoNotice(stopId: number): void {
    const cache = this.loadInfoCache();
    const key = String(stopId);
    if (cache[key]) {
      delete cache[key];
      this.saveInfoCache(cache);
    }
  }
}
